
import { useTranslation } from "react-i18next"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Star, User, Eye } from "lucide-react"
import type { Review } from "@/api/reviews"

interface ReviewsListTableProps {
  reviews: Review[]
  isLoading: boolean
  onViewDetails: (reviewId: string) => void
  currentPage: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void
}

export function ReviewsListTable({
  reviews,
  isLoading,
  onViewDetails,
  currentPage,
  totalPages,
  total,
  onPageChange,
}: ReviewsListTableProps) {
  const { t } = useTranslation()

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-muted-foreground">
          {rating}/5
        </span>
      </div>
    )
  }

  const getReviewTypeBadge = (type: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline', label: string }> = {
      class: { variant: 'default', label: t('reviews.classReview') },
      staff: { variant: 'secondary', label: t('reviews.staffReview') },
      class_session: { variant: 'outline', label: t('reviews.sessionReview') },
    }

    const config = variants[type] || { variant: 'outline', label: type }

    return (
      <Badge variant={config.variant} className="capitalize">
        {config.label}
      </Badge>
    )
  }

  const getTargetName = (review: Review) => {
    if (review.classId) {
      return review.classId.label || review.classId.name
    }
    if (review.staffId) {
      return `${review.staffId.name} ${review.staffId.surname}`
    }
    return '-'
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reviews.date')}</TableHead>
                <TableHead>{t('reviews.member')}</TableHead>
                <TableHead>{t('reviews.reviewType')}</TableHead>
                <TableHead>{t('reviews.target')}</TableHead>
                <TableHead>{t('reviews.rating')}</TableHead>
                <TableHead>{t('reviews.feedback')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-4 w-20 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-28 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-48 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center">
        <p className="text-muted-foreground">{t('reviews.noReviews')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('reviews.date')}</TableHead>
              <TableHead>{t('reviews.member')}</TableHead>
              <TableHead>{t('reviews.reviewType')}</TableHead>
              <TableHead>{t('reviews.target')}</TableHead>
              <TableHead>{t('reviews.rating')}</TableHead>
              <TableHead>{t('reviews.feedback')}</TableHead>
              <TableHead>{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review._id}>
                <TableCell className="whitespace-nowrap">
                  {new Date(review.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {review.isAnonymous ? (
                      <>
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {t('reviews.anonymous')}
                        </span>
                      </>
                    ) : (
                      <span>{review.userName}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{getReviewTypeBadge(review.reviewType)}</TableCell>
                <TableCell>{getTargetName(review)}</TableCell>
                <TableCell>{renderStars(review.rating)}</TableCell>
                <TableCell>
                  <div className="max-w-xs truncate">
                    {review.feedback || '-'}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(review._id)}
                    className="gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {t('reviews.viewDetails')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {t('common.pageInfo', {
            currentPage,
            totalPages,
            totalItems: total,
          })}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}
